import { useEffect, useRef, useState } from 'react';
import { BaseellaStatus, EnumExchangeTabs } from '../types';
import TransactionStatusChange from '../incoming-outgoing-transaction-status-change';
import Tabs from '../../tabs/tabs';
import ExchangeGeneralContent from './exchange-details';
import TransactionSignatures from '../signatures/signatures';
import TransactionLogsContent from '../transaction-logs-content';
import ChangeLogs from '../../change-logs/change-logs';
import TransactionDocuments from '../documents/documents';
import TransactionAccountingEntries from '../accounting-entries/accounting-entries';
import IconAttention from '../../icons/attention';
import ButtonPrimary from '../../button/button-primary';
import ButtonSecondary from '../../button/button-secondary';
import TransactionChats from '../chats/chats';
import TransactionComments from '../comments/comments';
import { PermissionEnum } from '../../../base-types';
import formatDate from '../../../helpers/format-date';
import { ExchangeContentTypes } from './types';
import SyncedTransactions from './synced-transactions';
import IntegrationLogs from '../integration-logs/list';
import PermissionDenied from '../../permission-denied/permission-denied';
import CreationDisabledInfo from '../disable-creation-option/creation-disabled-info';

import styles from '../transaction.module.css';

export default function ExchangeContent({
  control,
  transactionInfo,
  recipientAccounts,
  senderAccounts,
  currencies,
  isEditable,
  onSearchAutocomplete,
  logs,
  isCreateForm,
  rateSources,
  statusesInfo,
  onRollbackStatus,
  onNextStatus,
  senderAccountsIsLoading,
  recipientAccountsIsLoading,
  currencyFromId,
  currencyToId,
  fromAccountBalance,
  balanceFromAccountIsLoading,
  toAccountBalance,
  balanceToAccountIsLoading,
  errors,
  exchangeType,
  setValue,
  senderAccountNumber,
  calculateResult,
  baseellaStatus,
  changeLogs,
  exchangeCurrencyFixedId,
  isLoading,
  accountCategories,
  accountsCategoriesLoading,
  setSenderAccountQuery,
  setSenderAccounts,
  setRecipientAccountQuery,
  setRecipientAccounts,
  signatures,
  sendCode,
  setRejectPopupVisible,
  parentRef,
  parentScroll = 0,
  updateStatusIsLoading,
  isLoadingStatuses,
  latestQueuedStatus,
  createdByOrganizationId,
  companyOfficials,
  chats,
  onCreateTransactionChat,
  chatCreateInProgress,
  currentEmployee,
  documents,
  setSubPanelVisible,
  setSelectedDocumentName,
  setTransactionSidepanelVisible,
  onUploadDocument,
  uploadDocumentInProgress,
  uploadDocumentIsSuccess,
  uploadDocumentErrors,
  selectedDocumentId,
  setSelectedDocumentId,
  downloadInProgress,
  tabs,
  activeTab,
  setActiveTab,
  glTransactionsIsLoading,
  glTransactions,
  setGlTransactionSidePanelVisible,
  onGlTransactionClick,
  onDownloadDocument,
  getTransactionDocument,
  activePermission,
  canSignTransaction,
  onUpdateRates,
  calculateInProgress,
  providersRate,
  sendCodeInProgress,
  calcError,
  fromAccountBalances,
  toAccountBalances,
  currenciesList,
  transactionId,
  fields,
  calculateTransactionRatesByIdAction,
  caspEnabled,
  transactionChatsIsLoading,
  isLoadingSignatures,
  isLoadingLogs,
  getLogsTransactionById,
  getGeneralLedgerTransactionValues,
  getTransactionsChats,
  getCompanyOfficials,
  getSignaturesTransactionById,
  overdraftEnabled,
  baseRateSource,
  calculateOnSwap,
  getTransactionChangeLogs,
  changeLogsIsLoading,
  users,
  transactionChangeLogsPagination,
  changeLogsIsFetching,
  setInverseRateChanged,
  exchangeDateOfRate,
  transactionComments,
  transactionCommentsPagination,
  getTransactionCommentsAction,
  commentsIsLoading,
  commentsIsFetching,
  onAddCommentToTransaction,
  addCommentInProgress,
  addCommentIsSuccess,
  onEditCommentInTransaction,
  editTransactionCommentInProgress,
  editTrancastionCommentIsSuccess,
  onDeleteCommentInTransaction,
  deleteCommentInTransactionInProgress,
  deleteCommentInTranscationIsSuccess,
  syncedTransactionsIds,
  getSyncedTransactions,
  syncedTransactionsIsLoading,
  syncedTransactionsIsFetching,
  syncedTransactions,
  getIntegrationLogs,
  integrationLogsIsLoading,
  integrationLogsIsFetching,
  integrationLogs,
  integrationLogsPagination,
  setError,
  clearErrors,
  withTime,
  setWithTime,
}: ExchangeContentTypes): React.JSX.Element {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [signatureValid, setSignatureValid] = useState(true);

  useEffect(() => {
    if (
      activeTab === EnumExchangeTabs.LOG &&
      getLogsTransactionById &&
      activePermission?.includes(
        PermissionEnum.TRANSACTION_QUEUE_ITEMS_EXCHANGE_LIST,
      )
    ) {
      getLogsTransactionById();
    }

    if (
      activeTab === EnumExchangeTabs.CHATS &&
      getTransactionsChats &&
      activePermission?.includes(PermissionEnum.CHANNELS_LIST)
    ) {
      getTransactionsChats();

      if (
        transactionInfo &&
        transactionInfo.senderAccount &&
        transactionInfo.senderAccount?.organization?.type === 'CORPORATE' &&
        getCompanyOfficials
      ) {
        getCompanyOfficials(transactionInfo.senderAccount.organizationId);
      }
    }

    if (
      activeTab === EnumExchangeTabs.GL_TRANSACTIONS &&
      getGeneralLedgerTransactionValues
    ) {
      getGeneralLedgerTransactionValues();
    }

    if (
      activeTab === EnumExchangeTabs.CHANGE_LOG &&
      getTransactionChangeLogs &&
      activePermission?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getTransactionChangeLogs();
    }

    if (
      activeTab === EnumExchangeTabs.SIGNATURES &&
      getSignaturesTransactionById
    ) {
      getSignaturesTransactionById();
    }

    if (
      activeTab === EnumExchangeTabs.COMMENTS &&
      getTransactionCommentsAction &&
      activePermission?.includes(
        PermissionEnum.TRANSACTIONS_EXCHANGE_COMMENTS_LIST,
      )
    ) {
      getTransactionCommentsAction();
    }

    if (
      activeTab === EnumExchangeTabs.SYNCED_TRANSACTIONS &&
      syncedTransactionsIds?.length &&
      getSyncedTransactions
    ) {
      getSyncedTransactions(syncedTransactionsIds);
    }
  }, [activeTab]);

  useEffect(() => {
    if (
      (transactionInfo?.baseellaStatus === BaseellaStatus.NEW ||
        transactionInfo?.baseellaStatus === BaseellaStatus.FX_RATE_REQUEST) &&
      getSignaturesTransactionById
    ) {
      getSignaturesTransactionById();
    }
  }, [transactionInfo]);

  useEffect(() => {
    if (
      signatures &&
      signatures?.length > 0 &&
      (transactionInfo?.baseellaStatus === BaseellaStatus.NEW ||
        transactionInfo?.baseellaStatus === BaseellaStatus.FX_RATE_REQUEST)
    ) {
      // eslint-disable-next-line no-unsafe-optional-chaining
      const lastSignature = signatures[signatures?.length - 1];

      if (lastSignature) {
        setSignatureValid(lastSignature.valid as boolean);
      }
    }
  }, [signatures, transactionInfo]);

  return (
    <>
      {(baseellaStatus === BaseellaStatus.NOT_SIGNED ||
        ((baseellaStatus === BaseellaStatus.NEW ||
          baseellaStatus === BaseellaStatus.FX_RATE_REQUEST) &&
          !signatureValid)) &&
        canSignTransaction && (
          <div className={styles.notSignedAlert}>
            <div className={styles.notSignedText}>
              <IconAttention width={24} height={24} />
              Additional signatures required
            </div>
            <div className={styles.notSignedControls}>
              {!createdByOrganizationId && (
                <ButtonPrimary
                  text={sendCodeInProgress ? 'Loading...' : 'Sign'}
                  onClick={sendCode}
                  className={styles.signButton}
                  disabled={sendCodeInProgress}
                />
              )}
              {baseellaStatus === BaseellaStatus.NOT_SIGNED && (
                <ButtonSecondary
                  text="Reject"
                  onClick={() =>
                    setRejectPopupVisible && setRejectPopupVisible(true)
                  }
                  tabIndex={-1}
                />
              )}
            </div>
          </div>
        )}
      {statusesInfo && !transactionInfo?.deletedAt && (
        <TransactionStatusChange
          transaction={statusesInfo}
          onNextStatus={onNextStatus}
          baseellaStatus={baseellaStatus}
          onRollbackStatus={onRollbackStatus}
          updateStatusIsLoading={updateStatusIsLoading}
          isLoadingStatuses={isLoadingStatuses}
          latestQueuedStatus={latestQueuedStatus}
          calcError={calcError}
          activeTab={activeTab}
        />
      )}
      {transactionInfo?.deletedAt && (
        <div className={styles.deleted}>{`Transaction deleted ${formatDate(
          transactionInfo?.deletedAt,
          true,
        )}`}</div>
      )}
      {!isCreateForm && (
        <Tabs
          tabs={tabs}
          onClick={(value) => {
            setActiveTab(value as EnumExchangeTabs);

            if (parentScroll >= Number(scrollRef.current?.offsetTop) - 130) {
              parentRef?.current?.scrollTo({
                top: Number(scrollRef.current?.offsetTop) - 130,
              });
            }
          }}
          activeTab={activeTab}
          itemClassName={styles.transactionTab}
          tabsClassName={styles.exchangeTabs}
          moreButtonClassName={styles.transactionMoreButton}
          isTransactionsTabs
          withSticky
          isExchangeTabs
          // eslint-disable-next-line react-hooks/refs
          withShadow={parentScroll > Number(scrollRef.current?.offsetTop) - 130}
        />
      )}
      {transactionId && fields?.disableExternalTransactionCreate && (
        <CreationDisabledInfo />
      )}
      <div
        ref={scrollRef}
        className={`${
          activeTab === EnumExchangeTabs.GL_TRANSACTIONS
            ? styles.sidepanelContentWrapper
            : ''
        } ${styles.siddepanelContentFlex}`}
      >
        <div
          className={
            activeTab === EnumExchangeTabs.GENERAL
              ? styles.activeTabContent
              : styles.tabContent
          }
        >
          <ExchangeGeneralContent
            transactionInfo={transactionInfo}
            control={control}
            currencies={currencies}
            errors={errors}
            isEditable={isEditable}
            recipientAccounts={recipientAccounts}
            senderAccounts={senderAccounts}
            onSearchAutocomplete={onSearchAutocomplete}
            rateSources={rateSources}
            senderAccountsIsLoading={senderAccountsIsLoading}
            recipientAccountsIsLoading={recipientAccountsIsLoading}
            currencyFromId={currencyFromId}
            currencyToId={currencyToId}
            fromAccountBalance={fromAccountBalance}
            balanceFromAccountIsLoading={balanceFromAccountIsLoading}
            toAccountBalance={toAccountBalance}
            balanceToAccountIsLoading={balanceToAccountIsLoading}
            exchangeType={exchangeType}
            setValue={setValue}
            senderAccountNumber={senderAccountNumber}
            calculateResult={calculateResult}
            baseellaStatus={baseellaStatus}
            isLoading={isLoading}
            exchangeCurrencyFixedId={exchangeCurrencyFixedId}
            accountCategories={accountCategories}
            accountsCategoriesLoading={accountsCategoriesLoading}
            exchangeDateOfRate={exchangeDateOfRate}
            setSenderAccountQuery={setSenderAccountQuery}
            setSenderAccounts={setSenderAccounts}
            setRecipientAccountQuery={setRecipientAccountQuery}
            setRecipientAccounts={setRecipientAccounts}
            onUpdateRates={onUpdateRates}
            calculateInProgress={calculateInProgress}
            providersRate={providersRate}
            calcError={calcError}
            fromAccountBalances={fromAccountBalances}
            toAccountBalances={toAccountBalances}
            currenciesList={currenciesList}
            transactionId={transactionId}
            calculateTransactionRatesByIdAction={
              calculateTransactionRatesByIdAction
            }
            fields={fields}
            caspEnabled={caspEnabled}
            overdraftEnabled={overdraftEnabled}
            baseRateSource={baseRateSource}
            calculateOnSwap={calculateOnSwap}
            setInverseRateChanged={setInverseRateChanged}
            setError={setError}
            clearErrors={clearErrors}
            withTime={withTime}
            setWithTime={setWithTime}
          />
        </div>

        <div
          className={
            activeTab === EnumExchangeTabs.LOG
              ? styles.activeTabContent
              : styles.tabContent
          }
        >
          <TransactionLogsContent
            isLoading={isLoadingLogs as boolean}
            logs={logs}
            activePermission={activePermission?.includes(
              PermissionEnum.TRANSACTION_QUEUE_ITEMS_EXCHANGE_LIST,
            )}
          />
        </div>
        <div
          className={
            activeTab === EnumExchangeTabs.CHANGE_LOG
              ? styles.activeTabContent
              : styles.tabContent
          }
        >
          <ChangeLogs
            entityType="TRANSACTION"
            entityId={transactionId as string}
            changeLogsIsLoading={changeLogsIsLoading}
            changeLogs={changeLogs}
            activePermission={activePermission}
            users={users}
            pagination={transactionChangeLogsPagination}
            changeLogsIsFetching={changeLogsIsFetching}
            getChangeLogs={getTransactionChangeLogs}
          />
        </div>
        <div
          className={
            activeTab === EnumExchangeTabs.SIGNATURES
              ? styles.activeTabContent
              : styles.tabContent
          }
        >
          <TransactionSignatures
            signatures={signatures}
            isLoadingSignatures={isLoadingSignatures}
          />
        </div>
        <div
          className={
            activeTab === EnumExchangeTabs.GL_TRANSACTIONS
              ? styles.activeTabContent
              : styles.tabContent
          }
        >
          <TransactionAccountingEntries
            glTransactionsIsLoading={glTransactionsIsLoading}
            glTransactions={glTransactions}
            setGlTransactionSidePanelVisible={setGlTransactionSidePanelVisible}
            onGlTransactionClick={onGlTransactionClick}
            setTransactionSidepanelVisible={setTransactionSidepanelVisible}
            currenciesList={currenciesList}
          />
        </div>
        <div
          className={
            activeTab === EnumExchangeTabs.SYNCED_TRANSACTIONS
              ? styles.activeTabContent
              : styles.tabContent
          }
        >
          <SyncedTransactions
            syncedTransactionsIsLoading={syncedTransactionsIsLoading}
            syncedTransactionsIsFetching={syncedTransactionsIsFetching}
            syncedTransactions={syncedTransactions}
            currencies={currenciesList}
          />
        </div>
        <div
          className={
            activeTab === EnumExchangeTabs.DOCUMENTS
              ? styles.activeTabContent
              : styles.tabContent
          }
        >
          <TransactionDocuments
            documents={documents}
            setSubPanelVisible={setSubPanelVisible}
            setSelectedDocumentName={setSelectedDocumentName}
            setTransactionSidepanelVisible={setTransactionSidepanelVisible}
            onUploadDocument={onUploadDocument}
            uploadDocumentInProgress={uploadDocumentInProgress}
            uploadDocumentIsSuccess={uploadDocumentIsSuccess}
            uploadDocumentErrors={uploadDocumentErrors}
            selectedDocumentId={selectedDocumentId}
            setSelectedDocumentId={setSelectedDocumentId}
            downloadInProgress={downloadInProgress}
            activePermission={activePermission}
            onDownloadDocument={onDownloadDocument}
            getTransactionDocument={getTransactionDocument}
          />
        </div>
        <div
          className={
            activeTab === EnumExchangeTabs.CHATS
              ? styles.activeTabContent
              : styles.tabContent
          }
        >
          <TransactionChats
            chats={chats}
            companyOfficials={companyOfficials}
            individualCustomer={transactionInfo?.senderAccount}
            onCreateTransactionChat={onCreateTransactionChat}
            chatCreateInProgress={chatCreateInProgress}
            currentEmployee={currentEmployee}
            activePermissions={activePermission}
            transactionChatsIsLoading={transactionChatsIsLoading}
          />
        </div>
        <div
          className={
            activeTab === EnumExchangeTabs.INTEGRATION_LOGS
              ? styles.activeTabContent
              : styles.tabContent
          }
        >
          {getIntegrationLogs &&
          activePermission?.includes(
            PermissionEnum.PSP_INTEGRATION_LOGS_LIST,
          ) ? (
            <IntegrationLogs
              getIntegrationLogs={getIntegrationLogs}
              integrationLogs={integrationLogs}
              integrationLogsIsFetching={integrationLogsIsFetching}
              integrationLogsIsLoading={integrationLogsIsLoading}
              integrationLogsPagination={integrationLogsPagination}
              activePermissions={activePermission}
              activeTab={activeTab}
              transactionId={transactionId}
            />
          ) : (
            <>
              <div className={styles.changeLogsTitle}>Integration log</div>
              <PermissionDenied />
            </>
          )}
        </div>
        <div
          className={
            activeTab === EnumExchangeTabs.COMMENTS
              ? styles.activeTabContent
              : styles.tabContent
          }
        >
          <TransactionComments
            comments={transactionComments}
            pagination={transactionCommentsPagination}
            isLoading={commentsIsLoading}
            isFetching={commentsIsFetching}
            commentsAvailable={activePermission?.includes(
              PermissionEnum.TRANSACTIONS_EXCHANGE_COMMENTS_LIST,
            )}
            getCommentsList={getTransactionCommentsAction}
            createAvailable={activePermission?.includes(
              PermissionEnum.TRANSACTIONS_EXCHANGE_COMMENTS_CREATE_ACTION,
            )}
            editAvailable={activePermission?.includes(
              PermissionEnum.TRANSACTIONS_EXCHANGE_COMMENTS_EDIT_ACTION,
            )}
            deleteAvailable={activePermission?.includes(
              PermissionEnum.TRANSACTIONS_EXCHANGE_COMMENTS_DELETE_ACTION,
            )}
            onAddCommentToTransaction={onAddCommentToTransaction}
            addCommentInProgress={addCommentInProgress}
            addCommentIsSuccess={addCommentIsSuccess}
            onEditCommentInTransaction={onEditCommentInTransaction}
            editTransactionCommentInProgress={editTransactionCommentInProgress}
            editTrancastionCommentIsSuccess={editTrancastionCommentIsSuccess}
            users={users}
            onDeleteCommentInTransaction={onDeleteCommentInTransaction}
            deleteCommentInTransactionInProgress={
              deleteCommentInTransactionInProgress
            }
            deleteCommentInTranscationIsSuccess={
              deleteCommentInTranscationIsSuccess
            }
            changeLogsAvailable={activePermission?.includes(
              PermissionEnum.AUDIT_LOG_LIST,
            )}
          />
        </div>
      </div>
    </>
  );
}
